import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { InputGroup, Select, Icon } from '@wmgtech/legato'

import { cn } from 'shared/utils'
import { FormSelect } from 'components/FormWithGroup'
import { useSpaces } from 'modulesCommon/serviceHooks'

import { ISpaceOption } from '../../serviceHooks/useSpaces'
import { IUserForm } from 'modules/Users/types'

interface ISpace {
  id: string
  name: string
  labels: {
    alias: string
  }[]
}

export function spaceOptionAdapter(data: ISpace) {
  return {
    id: data?.id ?? '',
    name: data?.name ?? '',
    subtitle: (data?.labels ?? []).map((label) => label?.alias ?? '')
  }
}

export const SpacesField: FC = () => {

  const { spacesOptions, getCountryOptions, getSpaceById } = useSpaces(spaceOptionAdapter)

  const {
    field: { ref, ...restField },
    fieldState: { error }
  } = useController<IUserForm, 'space_id'>({ name: 'space_id' })

  const { control, setValue, getValues } = useFormContext<IUserForm>()

  const handleChange = useCallback(
    (el?: ISpaceOption) => {
      if (typeof el === 'undefined') {
        restField.onChange('')
      } else {
        restField.onChange(el.id)
      }
    },
    [restField],
  )

  const countryOptions = useMemo(
    () => getCountryOptions(restField.value),
    [getCountryOptions, restField.value],
  )

  const countryValues = useMemo(
    () => countryOptions.map(({ value }) => value),
    [countryOptions]
  )
  
  useEffect(
    () => {
      const currentCountryCode = getValues('country_code')
      if (countryOptions.length === 0) return
      if (typeof currentCountryCode === 'undefined' || !countryValues.includes(currentCountryCode)) {
        setValue('country_code', countryOptions[0]?.value || '')
      }
    },
    [countryOptions, countryValues, getValues, restField.value, setValue],
  )
    
  useEffect(
    () => {
      return () => {
        restField.onChange(undefined)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const currentSpace = useCallback<() => ISpaceOption>(
    () => {
      if (restField.value) {
        const space = getSpaceById(restField.value)
        if (typeof space !== 'undefined') return spaceOptionAdapter(space)
      }
      throw new Error('No space available')
    },
  [getSpaceById, restField.value],
  )

  const createElement = useCallback(
    (data: ISpaceOption) => <div className="user-form__option">{data.name}<span>({data.subtitle.join(', ')})</span></div>,
    [],
  )

  const spacesValue = useCallback(
    ({ isOpen }) => {
      const value = restField.value ? createElement(currentSpace()) : 'Select Space'
      return (
        <div
          className={cn('lgt-input lgt-input--md lgt-input--outline lgt-input--icons-1 lgt-select__control user-form-space__field', {
            'user-form-space__field--placeholder': !restField.value,
            'user-form-space__field--open': isOpen,
          })}
        >{value}<Icon name="chevron-down"/></div>
      )
    },
    [createElement, currentSpace, restField.value]
  )

  return (
    <>
      <InputGroup
        label="Spaces"
        className={cn('form-input-group', { 'form-input-group__error': error })}
        labelProps={{ icon: 'asterisk' }}
        validation={{
          message: error?.message,
          ...(error && { type: 'error' })
        }}
      >
        <Select<ISpaceOption>
          list={spacesOptions}
          getKey={el => el.id}
          getElement={createElement}
          onSelect={handleChange}
          value={restField.value}
          name={restField.name}
        >
          {
            ({ isOpen }) => spacesValue({ isOpen })
          }
        </Select>
      </InputGroup>
      <FormSelect
        label="Default Country Code"
        placeholder="Select Code"
        fieldName="country_code"
        disabled={!restField.value}
        options={countryOptions}
        control={control}
        rules={{ required: true }}
      />
    </>
  )
}
